import React, { useState, useCallback, useMemo } from 'react'
import {
  SimpleGrid,
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  useToast,
} from '@chakra-ui/react'
import { FaFolder } from 'react-icons/fa'
import { FiEdit2, FiInfo, FiShare2, FiDownload } from 'react-icons/fi'
import { Folder } from '@/types'
import { useNavigation } from '@/hooks/useNavigation'
import { useAppDispatch } from '@/store'
import { updateFolder } from '@/store/slices/foldersSlice'
import EditFolderModal from '@/components/Modals/EditFolderModal'
import FolderPropertiesModal from '@/components/Modals/FolderPropertiesModal'
import FolderPermissionsModal from '@/components/Modals/FolderPermissionsModal'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { MotionBox } from '@/components/Common/MotionBox'
import { ContextMenu } from '@/components/Common'
import { apiService } from '@/services/api'

interface SubfoldersGridProps {
  subfolders: Folder[]
  onFolderSelect: (folder: Folder | null) => void
  fileCount?: number
}

interface SubfolderCardProps {
  folder: Folder
  onFolderSelect: (folder: Folder | null) => void
  index: number
}

function SubfolderCard({ folder, onFolderSelect }: SubfolderCardProps) {
  const { moveSelectedFiles, moveFolder, moveFile } = useNavigation()
  const dispatch = useAppDispatch()
  const toast = useToast()
  const { t } = useAppTranslation(['files', 'common'])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isBeingDragged, setIsBeingDragged] = useState(false)

  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false)

  // Properties modal state
  const [showPropertiesModal, setShowPropertiesModal] = useState(false)

  // Permissions modal state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false)
    }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    try {
      const rawData = e.dataTransfer.getData('application/json')
      if (!rawData) {
        console.error('No drag data found')
        return
      }

      const dragData = JSON.parse(rawData)
      const { type, id, name } = dragData

      // Prevent dropping on itself
      if (type === 'folder' && id === folder.id) {
        return
      }

      let success = false
      if (type === 'files') {
        // Handle dragged files specifically
        const { fileIds } = dragData
        if (fileIds && fileIds.length > 0) {
          // Move each file individually
          const results = await Promise.all(
            fileIds.map((fileId: number) => moveFile(fileId, folder.id))
          )
          success = results.every(Boolean)

          if (success) {
            const fileCount = fileIds.length
            toast({
              title: 'Files Moved',
              description: `${fileCount} file${fileCount > 1 ? 's' : ''} moved to "${folder.name}"`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            })
          }
        } else {
          // Fallback to selected files if no specific fileIds
          success = await moveSelectedFiles(folder.id)

          if (success) {
            const fileCount = dragData.fileIds?.length || 1
            toast({
              title: 'Files Moved',
              description: `${fileCount} file${fileCount > 1 ? 's' : ''} moved to "${folder.name}"`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            })
          }
        }
      } else if (type === 'file') {
        // Single file from tree
        success = await moveFile(id, folder.id)

        if (success) {
          toast({
            title: 'File Moved',
            description: `"${name}" moved to "${folder.name}"`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
      } else if (type === 'folder') {
        success = await moveFolder(id, folder.id)

        if (success) {
          toast({
            title: 'Folder Moved',
            description: `"${name}" moved to "${folder.name}"`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
      }

      if (!success && (type === 'files' || type === 'file' || type === 'folder')) {
        toast({
          title: 'Move Failed',
          description: `Failed to move ${type === 'files' ? 'files' : type === 'file' ? `"${name}"` : `"${name}"`}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    } catch (error) {
      console.error('Error parsing drag data:', error)
      toast({
        title: 'Move Failed',
        description: 'Invalid drag data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // Drag handlers for folders
  const handleDragStart = (e: React.DragEvent) => {
    // Create custom drag image
    const dragImage = document.createElement('div')
    dragImage.style.position = 'absolute'
    dragImage.style.top = '-1000px'
    dragImage.style.left = '-1000px'
    dragImage.style.padding = '8px 12px'
    dragImage.style.backgroundColor = '#2b6cb0'
    dragImage.style.color = 'white'
    dragImage.style.borderRadius = '8px'
    dragImage.style.fontSize = '14px'
    dragImage.style.fontWeight = '600'
    dragImage.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
    dragImage.textContent = `📁 ${t('folderTree.moving_folder', { ns: 'files' })}`

    // Add drag image to DOM temporarily
    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 60, 20)

    // Clean up drag image after drag starts
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage)
      }
    }, 0)

    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type: 'folder',
        id: folder.id,
        name: folder.name,
      })
    )
    e.dataTransfer.effectAllowed = 'move'
    setIsBeingDragged(true)
    e.stopPropagation() // Prevent triggering click handler
  }

  const handleDragEnd = () => {
    setIsBeingDragged(false)
  }

  // Context menu handlers
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    setContextMenuPosition({
      x: event.clientX,
      y: event.clientY,
    })
    setShowContextMenu(true)
  }, [])

  const handleCloseContextMenu = useCallback(() => {
    setShowContextMenu(false)
  }, [])

  const handleContextMenuRename = useCallback(() => {
    setShowEditModal(true)
    handleCloseContextMenu()
  }, [])

  const handleContextMenuProperties = useCallback(() => {
    setShowPropertiesModal(true)
    handleCloseContextMenu()
  }, [handleCloseContextMenu])

  const handleDownloadFolder = useCallback(() => {
    const downloadUrl = apiService.getFolderDownloadZipUrl(folder.id)
    window.open(downloadUrl, '_blank')
    handleCloseContextMenu()
    toast({
      title: t('folders.downloadStarted', { ns: 'files' }),
      description: t('folders.downloadStartedDescription', { ns: 'files', folderName: folder.name }),
      status: 'info',
      duration: 5000,
      isClosable: true,
    })
  }, [folder.id, folder.name, handleCloseContextMenu, toast, t])

  const handleFolderUpdate = useCallback(
    async (updatedFolder: Folder) => {
      try {
        await dispatch(
          updateFolder({
            folderId: folder.id,
            data: { name: updatedFolder.name, description: updatedFolder.description },
          })
        ).unwrap()

        toast({
          title: 'Folder updated',
          description: `"${updatedFolder.name}" has been updated successfully.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } catch (error) {
        toast({
          title: 'Error updating folder',
          description: 'Failed to update folder. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    },
    [dispatch, folder.id, toast]
  )

  const handleClick = () => {
    // Only trigger folder selection if not dragging
    if (!isBeingDragged) {
      onFolderSelect(folder)
    }
  }

  return (
    <MotionBox whileHover={{ y: -2, scale: 1.02 }} whileTap={{ scale: 0.98 }}>
      <Box
        p={3}
        bg="gray.50"
        borderRadius="xl"
        border="2px"
        borderColor={isDragOver ? 'blue.400' : 'gray.200'}
        borderStyle={isDragOver ? 'dashed' : 'solid'}
        // boxShadow="sm"
        h="full"
        cursor="pointer"
        opacity={isBeingDragged ? 0.5 : 1}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        _hover={{
          borderColor: isDragOver ? 'blue.400' : 'blue.300',
          // boxShadow: 'md',
          // bg: isDragOver ? 'blue.100' : 'blue.25',
          transform: !isBeingDragged ? 'translateY(-2px)' : 'none',
          bg: isDragOver ? 'gray.200' : 'gray.100',
        }}
        transition="all 0.2s"
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <VStack spacing={1} align="center">
          <IconButton
            aria-label={`Open ${folder.name} folder`}
            icon={<FaFolder size="32px" />}
            size="lg"
            colorScheme="blue"
            variant="ghost"
            borderRadius="full"
            color="blue.500"
            pointerEvents="none"
            _hover={{}}
          />
          <VStack spacing={1} align="center">
            <HStack spacing={1} justify="center">
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color="gray.800"
                textAlign="center"
                noOfLines={2}
              >
                {folder.name}
              </Text>
            </HStack>
            {/* {folder.description && (
              <Text
                fontSize="sm"
                color="gray.600"
                textAlign="center"
                noOfLines={2}
              >
                {folder.description}
              </Text>
            )} */}
          </VStack>
        </VStack>
      </Box>

      {/* Context Menu */}
      <ContextMenu
        isOpen={showContextMenu}
        position={contextMenuPosition}
        onClose={handleCloseContextMenu}
        items={[
          {
            icon: FiEdit2,
            label: t('buttons.rename', { ns: 'common' }),
            onClick: handleContextMenuRename,
          },
          {
            icon: FiInfo,
            label: t('buttons.properties', { ns: 'common' }),
            onClick: handleContextMenuProperties,
          },
          {
            icon: FiDownload,
            label: t('folders.downloadAsZip', { ns: 'files' }),
            onClick: handleDownloadFolder,
          },
          {
            icon: FiShare2,
            label: t('buttons.share', { ns: 'common' }),
            onClick: () => setShowPermissionsModal(true),
          },
        ]}
      />

      {/* Edit Folder Modal */}
      <EditFolderModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        folder={folder}
        onUpdate={handleFolderUpdate}
      />

      {/* Folder Properties Modal */}
      <FolderPropertiesModal
        isOpen={showPropertiesModal}
        onClose={() => setShowPropertiesModal(false)}
        folderId={folder.id}
      />

      {/* Folder Permissions Modal */}
      <FolderPermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        folderId={folder.id}
        folderName={folder.name}
      />
    </MotionBox>
  )
}

export default function SubfoldersGrid({
  subfolders,
  onFolderSelect,
  fileCount,
}: SubfoldersGridProps) {
  const { t } = useAppTranslation(['files', 'common'])

  // Sort subfolders alphabetically for better UX, memoized for performance
  const sortedSubfolders = useMemo(() => {
    if (!subfolders || subfolders.length === 0) {
      return []
    }
    return [...subfolders].sort((a, b) => {
      // Sort by name alphabetically (case-insensitive)
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    })
  }, [subfolders])

  // The heading stays even with no subfolders — it labels the file table too.
  const counts = [
    sortedSubfolders.length > 0
      ? t('plurals.folder', { ns: 'common', count: sortedSubfolders.length })
      : null,
    fileCount !== undefined && fileCount > 0
      ? t('plurals.file', { ns: 'common', count: fileCount })
      : null,
  ].filter(Boolean)

  return (
    <Box mb={6}>
      <VStack spacing={4} align="stretch">
        <HStack spacing={3} align="baseline">
          <Text fontSize="sm" fontWeight="bold" color="gray.800">
            {t('fileExplorer.content', { ns: 'files' })}
          </Text>
          {counts.length > 0 && (
            <Text fontSize="xs" color="gray.500">
              {counts.join(' · ')}
            </Text>
          )}
        </HStack>

        {sortedSubfolders.length > 0 && (
          <SimpleGrid columns={{ base: 2, md: 3, lg: 5, xl: 6 }} spacing={4}>
            {sortedSubfolders.map((folder, index) => (
              <SubfolderCard
                key={folder.id}
                folder={folder}
                onFolderSelect={onFolderSelect}
                index={index}
              />
            ))}
          </SimpleGrid>
        )}
      </VStack>
    </Box>
  )
}
